import "./App.css";
import React, { useState, useRef, useCallback, useEffect } from "react";
import { useAuth } from "./contexts/AuthContext";
import { API_BASE } from "./config";
import OnboardingPage from "./pages/OnboardingPage";
import PdfPreview from "./components/PdfPreview";
import SpeedReader from "./components/SpeedReader";
import NotesPanel from "./components/NotesPanel";
import ProfileMenu from "./components/ProfileMenu";
import DocumentLibrary from "./components/DocumentLibrary";
import AuthModal from "./components/AuthModal";

// Re-exported so other files can import from App.js (legacy)
export { API_BASE } from "./config";

function App() {
  const { user, session, loading, isAuthConfigured } = useAuth();

  // Ensure user record exists in public.users whenever a session is active
  useEffect(() => {
    if (!session?.access_token) return;
    fetch(`${API_BASE}/api/auth/sync`, {
      method: "POST",
      headers: { Authorization: `Bearer ${session.access_token}` },
    }).catch(() => {});
  }, [session?.access_token]);

  // Handle Stripe redirect back to app
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sub = params.get("subscription");
    if (sub === "success") {
      setUploadError(null);
      window.history.replaceState({}, "", window.location.pathname);
      // Open My Documents so user sees their updated plan
      if (user) setShowDocLibrary(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Current PDF file: { file_id, filename, blobUrl, text }
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [cloudLimitNotice, setCloudLimitNotice] = useState(null);
  const [notesSaveError, setNotesSaveError] = useState(false);

  // Speed reader position (lifted so it survives tab switches on mobile)
  const [readerIndex, setReaderIndex] = useState(0);

  // PDF sync — set when reader pauses so PdfPreview scrolls + highlights
  const [targetPdfPage, setTargetPdfPage] = useState(null);
  const [readingLinePct, setReadingLinePct] = useState(null);
  const [highlightWord, setHighlightWord] = useState(null);

  // Notes state — keyed per document so switching files always gives a clean slate
  const emptyNotes = useCallback((title = "") => ({
    documentTitle: title,
    freeformContent: "",
    citations: [],
  }), []);

  const [notes, setNotes] = useState(() => emptyNotes());

  // When a new document is loaded, load that document's saved notes (or start fresh)
  useEffect(() => {
    if (!pdfFile) {
      setNotes(emptyNotes());
      return;
    }
    const key = pdfFile.file_id
      ? `spritz-notes-doc-${pdfFile.file_id}`
      : `spritz-notes-story-${pdfFile.filename}`;
    try {
      const saved = localStorage.getItem(key);
      setNotes(saved ? JSON.parse(saved) : emptyNotes(pdfFile.filename || ""));
    } catch {
      setNotes(emptyNotes(pdfFile.filename || ""));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfFile?.file_id, pdfFile?.filename]);

  // Persist notes under the current document's key whenever they change
  useEffect(() => {
    if (!pdfFile) return;
    const key = pdfFile.file_id
      ? `spritz-notes-doc-${pdfFile.file_id}`
      : `spritz-notes-story-${pdfFile.filename}`;
    try {
      localStorage.setItem(key, JSON.stringify(notes));
      setNotesSaveError(false);
    } catch {
      setNotesSaveError(true);
    }
  }, [notes, pdfFile]);

  const handleNotesChange = useCallback((update) => {
    setNotes((prev) => ({ ...prev, ...update }));
  }, []);

  // Document library
  const [showDocLibrary, setShowDocLibrary] = useState(false);
  const [prefetchedDocs, setPrefetchedDocs] = useState(null);
  const [prefetchedStorage, setPrefetchedStorage] = useState(null);

  // Pre-fetch documents in the background as soon as the user is logged in (streamed NDJSON)
  useEffect(() => {
    if (!session?.access_token) return;
    const headers = { Authorization: `Bearer ${session.access_token}` };
    let cancelled = false;

    const streamDocs = async () => {
      const res = await fetch(`${API_BASE}/api/reader/documents`, { headers });
      if (!res.ok) return;
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      const accumulated = [];
      while (true) {
        const { done, value } = await reader.read();
        if (cancelled) { reader.cancel(); return; }
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.trim()) continue;
          try { accumulated.push(JSON.parse(line)); } catch {}
          if (!cancelled) setPrefetchedDocs([...accumulated]);
        }
      }
      if (buffer.trim() && !cancelled) {
        try { accumulated.push(JSON.parse(buffer)); setPrefetchedDocs([...accumulated]); } catch {}
      }
    };

    streamDocs().catch(() => {});
    fetch(`${API_BASE}/api/reader/storage`, { headers })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (!cancelled && data) setPrefetchedStorage(data); })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [session?.access_token]);

  // Auth modal (sign in from within reader)
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Cloud notes sync — load from cloud when a document opens (logged in)
  useEffect(() => {
    if (!pdfFile?.file_id || !user || !session?.access_token) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/reader/documents/${pdfFile.file_id}/notes`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((cloudNotes) => {
        if (cancelled || !cloudNotes) return;
        // Only override local if cloud has actual content
        if (
          (cloudNotes.citations && cloudNotes.citations.length > 0) ||
          cloudNotes.freeformContent
        ) {
          setNotes((prev) => ({
            ...prev,
            freeformContent: cloudNotes.freeformContent ?? prev.freeformContent,
            citations: cloudNotes.citations ?? prev.citations,
          }));
        }
      })
      .catch(() => {}); // silently ignore — localStorage fallback is in place
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfFile?.file_id, user]);

  // Debounced cloud notes save
  const cloudSaveTimerRef = useRef(null);
  useEffect(() => {
    if (!pdfFile?.file_id || !user || !session?.access_token) return;
    clearTimeout(cloudSaveTimerRef.current);
    cloudSaveTimerRef.current = setTimeout(() => {
      fetch(`${API_BASE}/api/reader/documents/${pdfFile.file_id}/notes`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          documentTitle: notes.documentTitle || pdfFile.filename || "",
          freeformContent: notes.freeformContent || null,
          citations: notes.citations || [],
        }),
      }).catch(() => {}); // silently ignore — localStorage is the backup
    }, 2000);
    return () => clearTimeout(cloudSaveTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, pdfFile?.file_id, user]);

  const handleAddCitation = useCallback(({ text, wordIndex, wordContext }) => {
    const citation = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      text,
      wordIndex,
      wordContext,
      createdAt: new Date().toISOString(),
    };
    setNotes((prev) => ({ ...prev, citations: [...prev.citations, citation] }));
  }, []);

  // Mobile tab: 'pdf' | 'reader' | 'notes'
  const [activeMobileTab, setActiveMobileTab] = useState("reader");

  // Annotated PDF blob URL — regenerated in background when citations change
  const [annotatedBlobUrl, setAnnotatedBlobUrl] = useState(null);
  const annotateTimerRef = useRef(null);
  const annotateAbortRef = useRef(null);

  // Clear annotated URL when document changes
  useEffect(() => {
    if (annotatedBlobUrl) {
      URL.revokeObjectURL(annotatedBlobUrl);
      setAnnotatedBlobUrl(null);
    }
    clearTimeout(annotateTimerRef.current);
    if (annotateAbortRef.current) annotateAbortRef.current.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfFile?.file_id, pdfFile?.filename]);

  // Debounced background re-annotation whenever citations change
  useEffect(() => {
    clearTimeout(annotateTimerRef.current);
    if (annotateAbortRef.current) annotateAbortRef.current.abort();

    if (!pdfFile?.file_id || notes.citations.length === 0) {
      setAnnotatedBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      return;
    }

    annotateTimerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      annotateAbortRef.current = controller;
      try {
        const headers = { "Content-Type": "application/json" };
        if (session?.access_token) headers["Authorization"] = `Bearer ${session.access_token}`;
        const res = await fetch(`/api/files/annotate`, {
          method: "POST",
          headers,
          signal: controller.signal,
          body: JSON.stringify({
            file_id: pdfFile.file_id,
            pdf_url: pdfFile.pdfUrl || null,
            pageBreaks: pdfFile.pageBreaks || [],
            documentTitle: notes.documentTitle || pdfFile.filename || "",
            download_mode: "citations_only",
            include_summary: false,
            citations: notes.citations.map(({ text, wordIndex, wordContext }) => ({
              text,
              wordIndex,
              wordContext: wordContext || "",
            })),
          }),
        });
        if (!res.ok) {
          console.error("[annotate-proxy] background annotation failed:", res.status, await res.text().catch(() => ""));
          return;
        }
        const contentType = res.headers.get("content-type") || "";
        if (!contentType.includes("application/pdf")) {
          console.error("[annotate-proxy] unexpected content-type:", contentType);
          return;
        }
        const blob = await res.blob();
        const newUrl = URL.createObjectURL(blob);
        setAnnotatedBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return newUrl;
        });
        // Scroll PDF viewer to the page of the most recently added citation
        const lastCit = notes.citations[notes.citations.length - 1];
        if (lastCit) {
          const counts = pdfFile.pageWordCounts;
          if (counts && counts.length > 0) {
            let cum = 0;
            for (let p = 0; p < counts.length; p++) {
              cum += counts[p];
              if (lastCit.wordIndex < cum) {
                setTargetPdfPage((prev) => (prev === p + 1 ? -(p + 1) : p + 1));
                break;
              }
            }
          }
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("[annotate-proxy] background annotation error:", err);
        }
      }
    }, 600);

    return () => {
      clearTimeout(annotateTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes.citations, pdfFile?.file_id]);

  // Cloud nudge banner — dismissed per session
  const [nudgeDismissed, setNudgeDismissed] = useState(
    () => sessionStorage.getItem("cloud-nudge-dismissed") === "1"
  );
  const dismissNudge = useCallback(() => {
    sessionStorage.setItem("cloud-nudge-dismissed", "1");
    setNudgeDismissed(true);
  }, []);

  // Show onboarding for unauthenticated users; dismissed when they pick a story or upload
  const [showOnboarding, setShowOnboarding] = useState(true);

  const fileInputRef = useRef(null);

  // Revoke blob URL on unmount to free memory
  useEffect(() => {
    return () => {
      if (pdfFile?.blobUrl) URL.revokeObjectURL(pdfFile.blobUrl);
      if (annotatedBlobUrl) URL.revokeObjectURL(annotatedBlobUrl);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  const handleFileSelect = useCallback(async (file) => {
    if (!file) return;
    if (!file.type.includes("pdf") && !file.name.toLowerCase().endsWith(".pdf")) {
      setUploadError("Please select a PDF file.");
      return;
    }

    // Create blob URL immediately so the PDF viewer shows right away
    const blobUrl = URL.createObjectURL(file);

    // Revoke the previous blob URL
    if (pdfFile?.blobUrl) URL.revokeObjectURL(pdfFile.blobUrl);

    setUploading(true);
    setUploadError(null);
    setReaderIndex(0);

    // Optimistically set the PDF so it renders while text is being extracted
    setPdfFile({ file_id: null, filename: file.name, blobUrl, text: "" });

    try {
      const formData = new FormData();
      formData.append("file", file);

      const headers = {};
      if (session?.access_token) headers["Authorization"] = `Bearer ${session.access_token}`;

      const res = await fetch(`${API_BASE}/api/files/upload`, {
        method: "POST",
        headers,
        body: formData,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const detail = body.detail;
        throw new Error(
          Array.isArray(detail) ? detail.map((m) => m.msg || m).join("; ") : detail || "Upload failed"
        );
      }

      const data = await res.json();
      // data: { file_id, filename, path, extracted_chars, text, pageBreaks, page_word_counts, cloud_limit_reached }
      if (!data.text || data.text.trim().length === 0) {
        setUploadError("No readable text found in this PDF. It may be a scanned image. Try a text-based PDF.");
      }
      if (data.cloud_limit_reached) {
        setCloudLimitNotice(data.cloud_limit_reached);
      }
      setPdfFile({ file_id: data.file_id, filename: data.filename, blobUrl, text: data.text || "", pageBreaks: data.pageBreaks || [], pageWordCounts: data.page_word_counts || [] });
    } catch (err) {
      setUploadError(err.message || "Upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  }, [pdfFile, session]);

  // Called from OnboardingPage when user picks a story or uploads a PDF
  const handleEnterReader = useCallback(({ text, filename, blobUrl, file, fileId, pageBreaks, pageWordCounts }) => {
    setShowOnboarding(false);
    setReaderIndex(0);
    if (file) {
      // User uploaded a PDF from onboarding — run the full upload flow
      handleFileSelect(file);
    } else {
      if (pdfFile?.blobUrl) URL.revokeObjectURL(pdfFile.blobUrl);
      setPdfFile({
        file_id: fileId || null,
        filename,
        blobUrl: blobUrl || null,
        text: text || "",
        pageBreaks: pageBreaks || [],
        pageWordCounts: pageWordCounts || [],
      });
    }
  }, [pdfFile, handleFileSelect]); // eslint-disable-line react-hooks/exhaustive-deps

  // Called by PdfPreview when user clicks a page.
  // Uses the actual clicked span text to find the exact word, then walks back
  // to the sentence start so the reader begins at a natural boundary.
  const handlePdfPageClick = useCallback((pageNum, yPct, xPct = 0) => {
    const text = pdfFile?.text || "";
    const allWords = text.split(/\s+/).filter(Boolean);
    if (allWords.length === 0) return;

    const counts = pdfFile?.pageWordCounts;

    // Click in right margin (>74%) → user clicked an annotation box.
    // Navigate the reader to that citation's exact word index.
    if (xPct > 0.74 && notes.citations.length > 0 && counts && counts.length > 0) {
      let pageStart = 0;
      for (let i = 0; i < Math.min(pageNum - 1, counts.length); i++) {
        pageStart += counts[i] || 0;
      }
      const pageWordCount = counts[Math.min(pageNum - 1, counts.length - 1)] || 0;
      const pageEnd = pageStart + pageWordCount;

      // Citations on this page, ordered by wordIndex (matches backend sort order)
      const pageCits = notes.citations
        .filter(c => c.wordIndex >= pageStart && c.wordIndex < pageEnd)
        .sort((a, b) => a.wordIndex - b.wordIndex);

      if (pageCits.length > 0) {
        // Annotation boxes: y_start=56, box_h=54, gap=6 → total slot=60pt
        // Use ~840 as a reasonable estimate of page height in PDF points.
        const clickedYPt = yPct * 840;
        const boxIdx = Math.max(0, Math.min(
          pageCits.length - 1,
          Math.floor((clickedYPt - 56) / 60)
        ));
        const cit = pageCits[boxIdx];
        setReaderIndex(cit.wordIndex);
        setActiveMobileTab("reader");
        setHighlightWord(allWords[cit.wordIndex] || null);
        setTargetPdfPage(pageNum);
        setReadingLinePct(0);
        return;
      }
    }

    // Normal click: estimate word position from y-position within the page
    let pageStart = 0;
    let pageEnd = allWords.length;
    if (counts && counts.length > 0) {
      for (let i = 0; i < Math.min(pageNum - 1, counts.length); i++) {
        pageStart += counts[i] || 0;
      }
      pageEnd = pageStart + (counts[Math.min(pageNum - 1, counts.length - 1)] || 0);
    }
    const wordIdx = Math.max(0, Math.min(allWords.length - 1,
      pageStart + Math.round(yPct * (pageEnd - pageStart))
    ));

    // Snap back to the nearest sentence start (max 300 words)
    let sentenceStart = wordIdx;
    for (let i = wordIdx - 1; i >= Math.max(0, wordIdx - 300); i--) {
      if (/[.!?]['")\]]*$/.test(allWords[i])) { sentenceStart = i + 1; break; }
    }

    setReaderIndex(sentenceStart);
    setActiveMobileTab("reader");
    setHighlightWord(allWords[sentenceStart] || null);
    setTargetPdfPage(pageNum);
    setReadingLinePct(0);
  }, [pdfFile, notes.citations]);

  // Called by SpeedReader on pause — maps word index → PDF page + occurrence
  const handlePause = useCallback((wordIndex, word) => {
    const counts = pdfFile?.pageWordCounts;
    const text = pdfFile?.text || "";
    if (!counts || counts.length === 0) return;
    const allWords = text.split(/\s+/).filter(Boolean);
    const cleanedTarget = word.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    let cumulative = 0;
    for (let p = 0; p < counts.length; p++) {
      cumulative += counts[p];
      if (wordIndex < cumulative) {
        const pageStart = cumulative - counts[p];
        const wordIndexInPage = wordIndex - pageStart;
        let occurrenceNum = 0;
        for (let i = pageStart; i < pageStart + wordIndexInPage; i++) {
          if ((allWords[i] || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase() === cleanedTarget) {
            occurrenceNum++;
          }
        }
        setTargetPdfPage(p + 1);
        setReadingLinePct(occurrenceNum);
        setHighlightWord(word || null);
        break;
      }
    }
  }, [pdfFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  // ── Loading / Auth gate ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "var(--bg-page)" }}>
        <span style={{ color: "var(--text-muted)" }}>Loading...</span>
      </div>
    );
  }

  if (!user && showOnboarding) {
    return <OnboardingPage onEnterReader={handleEnterReader} />;
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      className={`split-workspace mobile-active-${activeMobileTab}`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      {/* ── Header ──────────────────────────────────────────────────── */}
      <header className="app-header">
        <span className="app-header-title">Flash Focus</span>
        <div className="app-header-actions">
          {pdfFile && (
            <span className="app-header-filename" title={pdfFile.filename}>
              {pdfFile.filename}
            </span>
          )}
          {uploadError && (
            <span className="app-header-error" title={uploadError}>{uploadError}</span>
          )}
          {cloudLimitNotice && (
            <span className="app-header-cloud-notice" title={cloudLimitNotice}>
              <button type="button" className="app-header-cloud-notice-open" onClick={() => { setShowDocLibrary(true); setCloudLimitNotice(null); }}>
                Not saved to cloud — view My Documents
              </button>
              <button type="button" className="app-header-cloud-notice-dismiss" onClick={() => setCloudLimitNotice(null)}>✕</button>
            </span>
          )}
          {notesSaveError && (
            <span className="app-header-error" title="Notes failed to save — your browser may be out of storage.">Notes not saving — storage full</span>
          )}

          {uploading ? (
            <span className="app-header-uploading">Uploading…</span>
          ) : (
            <button
              type="button"
              className="app-header-upload-btn"
              onClick={() => fileInputRef.current?.click()}
            >
              {pdfFile ? "Change PDF" : "Open PDF"}
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            style={{ display: "none" }}
            onChange={(e) => {
              if (e.target.files[0]) handleFileSelect(e.target.files[0]);
              e.target.value = "";
            }}
          />
          {isAuthConfigured && user && (
            <button
              type="button"
              className="app-header-upload-btn"
              onClick={() => setShowDocLibrary(true)}
            >
              My Documents
            </button>
          )}
          {isAuthConfigured && (
            user
              ? <ProfileMenu />
              : (
                <button
                  type="button"
                  className="app-header-signin-btn"
                  onClick={() => setShowAuthModal(true)}
                  title="Sign in to save your notes to the cloud"
                >
                  Sign in
                </button>
              )
          )}
        </div>
      </header>

      {/* ── Cloud nudge banner (logged-out users only) ───────────────── */}
      {isAuthConfigured && !user && !nudgeDismissed && (
        <div className="cloud-nudge">
          <span className="cloud-nudge-text">
            Sign in to save your documents to the cloud — so you can pick up where you left off on your phone.
          </span>
          <button type="button" className="cloud-nudge-signin" onClick={() => { setShowAuthModal(true); dismissNudge(); }}>
            Sign in
          </button>
          <button type="button" className="cloud-nudge-dismiss" onClick={dismissNudge} aria-label="Dismiss">✕</button>
        </div>
      )}

      {/* ── Body row ────────────────────────────────────────────────── */}
      <div className="split-workspace-body">

        {/* Left: PDF viewer */}
        <aside
          className="panel split-panel-pdf"
          aria-label="PDF viewer"
          aria-hidden={activeMobileTab !== "pdf" ? "true" : undefined}
        >
          <PdfPreview
            pdfUrl={annotatedBlobUrl || pdfFile?.blobUrl || null}
            pdfLoading={!!(pdfFile?.hasPdf && !pdfFile?.blobUrl && !annotatedBlobUrl)}
            targetPage={targetPdfPage}
            readingLinePct={readingLinePct}
            highlightWord={highlightWord}
            onPageClick={pdfFile ? handlePdfPageClick : undefined}
          />
        </aside>

        {/* Right column: Speed Reader + Notes */}
        <div className="split-panel-right">

          {/* Speed Reader */}
          <section
            className="panel reader-panel"
            aria-label="Speed reader"
            aria-hidden={activeMobileTab !== "reader" ? "true" : undefined}
          >
            <SpeedReader
              text={pdfFile?.text || ""}
              startIndex={readerIndex}
              onIndexChange={setReaderIndex}
              onPause={handlePause}
              onAddCitation={handleAddCitation}
            />
          </section>

          {/* Notes */}
          <section
            className="panel notes-outer-panel"
            aria-label="Notes"
            aria-hidden={activeMobileTab !== "notes" ? "true" : undefined}
          >
            <NotesPanel
              key={pdfFile?.file_id || pdfFile?.filename || "empty"}
              notes={notes}
              onNotesChange={handleNotesChange}
              fileId={pdfFile?.file_id}
              pdfUrl={pdfFile?.pdfUrl || null}
              pageBreaks={pdfFile?.pageBreaks || []}
              accessToken={session?.access_token || null}
            />
          </section>

        </div>
      </div>

      {/* ── Auth modal ───────────────────────────────────────────────── */}
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}

      {/* ── Document Library modal ───────────────────────────────────── */}
      {showDocLibrary && (
        <DocumentLibrary
          session={session}
          prefetchedDocs={prefetchedDocs}
          prefetchedStorage={prefetchedStorage}
          onClose={() => setShowDocLibrary(false)}
          onOpen={(doc) => {
            setShowDocLibrary(false);
            if (pdfFile?.blobUrl) URL.revokeObjectURL(pdfFile.blobUrl);
            setPdfFile({
              file_id: doc.file_id,
              filename: doc.filename,
              blobUrl: null,
              pdfUrl: doc.pdfUrl || null,
              hasPdf: doc.hasPdf || false,
              text: doc.text || "",
              pageBreaks: doc.pageBreaks || [],
              pageWordCounts: doc.pageWordCounts || [],
            });
            setReaderIndex(0);
            setShowOnboarding(false);
          }}
          onPdfReady={(fileId, blobUrl) => {
            setPdfFile((prev) => {
              if (!prev || prev.file_id !== fileId) {
                if (blobUrl) URL.revokeObjectURL(blobUrl);
                return prev;
              }
              if (prev.blobUrl) URL.revokeObjectURL(prev.blobUrl);
              return { ...prev, blobUrl, hasPdf: false };
            });
          }}
        />
      )}

      {/* ── Mobile bottom navigation ─────────────────────────────────── */}
      <nav className="mobile-bottom-nav" aria-label="Switch panel">
        <button
          type="button"
          className={`mobile-nav-btn ${activeMobileTab === "pdf" ? "mobile-nav-btn-active" : ""}`}
          onClick={() => setActiveMobileTab("pdf")}
          aria-current={activeMobileTab === "pdf" ? "true" : undefined}
        >
          <span className="mobile-nav-icon" aria-hidden>📄</span>
          <span>PDF</span>
        </button>
        <button
          type="button"
          className={`mobile-nav-btn ${activeMobileTab === "reader" ? "mobile-nav-btn-active" : ""}`}
          onClick={() => setActiveMobileTab("reader")}
          aria-current={activeMobileTab === "reader" ? "true" : undefined}
        >
          <span className="mobile-nav-icon" aria-hidden>⚡</span>
          <span>Reader</span>
        </button>
        <button
          type="button"
          className={`mobile-nav-btn ${activeMobileTab === "notes" ? "mobile-nav-btn-active" : ""}`}
          onClick={() => setActiveMobileTab("notes")}
          aria-current={activeMobileTab === "notes" ? "true" : undefined}
        >
          <span className="mobile-nav-icon" aria-hidden>📝</span>
          <span>Notes</span>
        </button>
      </nav>
    </div>
  );
}

export default App;
